/*
 * TYPEAHEAD COMPONENT
 * 
 * Generic typeahead/autocomplete component with support for multiple sections and custom filtering.
 * 
 * Key Features:
 * - Multi-section display with customizable titles and filtering
 * - Real-time search with debouncing (300ms)
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Visual highlighting of search terms
 * - Custom avatar colors per section
 * - Suggested contact detection and visual distinction
 * 
 * Enhanced Filtering Logic:
 * - `isSuggested`: Detects fake IDs (>= 99000000) for suggested contacts
 * - `hasId`: Legacy boolean filtering for backward compatibility  
 * - Proper handling of real vs fake contact IDs
 * 
 * Visual Distinction:
 * - Suggested contacts are distinguished only by section organization
 * - Uses Tailwind classes for consistent theming
 * - Respects dark mode through THEME_CLASSES
 * - Supports explicit accessible labels and scoped input styling independent of placeholder copy
 */

import React from 'react';
import { useState, useEffect, useRef, KeyboardEvent, useCallback } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { request } from '../../services/dataService';
import { highlightText } from '../../utils/highlightUtils';
import { Avatar } from './Avatar';

export interface TypeaheadResult {
  id: number;
  name: string;
  type: string;
  personId?: number;
  teamId?: number;
  accountId?: number | null;
  accountName?: string;
}

export interface SectionFilter {
  type: string;
  hasId?: boolean;
  isSuggested?: boolean;
}

export interface Section {
  title: string;
  filter: SectionFilter;
  itemClassName?: string;
  avatarColor?: string;
  renderItem?: (item: TypeaheadResult, index: number, isActive: boolean, searchValue: string) => React.ReactNode;
}

interface TypeaheadProps {
  onSelect: (result: TypeaheadResult) => void;
  sections: Section[];
  searchEndpoint?: string;
  searchParams?: Record<string, any>;
  results?: TypeaheadResult[];
  error?: string | null;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  inputClassName?: string;
  autoFocus?: boolean;
}

const Typeahead: React.FC<TypeaheadProps> = (props) => {
  const {
    onSelect,
    sections,
    searchEndpoint,
    searchParams,
    results: externalResults,
    error = null,
    placeholder = 'Search...',
    ariaLabel,
    className = '',
    inputClassName = '',
    autoFocus = false
  } = props;
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [internalResults, setInternalResults] = useState<TypeaheadResult[]>([]);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState('');

  const debouncedSearchValue = useDebounce(searchValue, 300);
  const abortControllerRef = useRef<AbortController | null>(null);
  const previousSearchRef = useRef<string>('');

  // Memoized fetch function
  const fetchResults = useCallback(async (
    value: string,
    endpoint: string,
    controller: AbortController,
    params: Record<string, any> | undefined
  ) => {
    try {
      const results = await request(endpoint, { ...(params || {}), q: value });
      return !controller.signal.aborted ? results : null;
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error('Search error:', err);
      }
      return null;
    }
  }, []);

  // Memoized search function
  const search = useCallback(async (searchValue: string) => {
    if (searchValue.length < 2 || !searchEndpoint) {
      return {
        results: [],
        loading: false,
        previousSearch: '',
        showDropdown: false
      };
    }

    if (searchValue === previousSearchRef.current) {
      return null;
    }

    const controller = abortControllerRef.current;
    if (controller) {
      controller.abort();
    }

    const newController = new AbortController();
    abortControllerRef.current = newController;

    const results = await fetchResults(
      searchValue,
      searchEndpoint,
      newController,
      searchParams
    );

    if (!newController.signal.aborted) {
      return {
        results: results || [],
        loading: false,
        previousSearch: searchValue,
        showDropdown: !!results && results.length > 0
      };
    }

    return null;
  }, [searchEndpoint, searchParams, fetchResults]);

  // Handle API calls when debounced value changes
  useEffect(() => {
    const updateState = async () => {
      const result = await search(debouncedSearchValue);
      if (result) {
        setInternalResults(result.results);
        setIsLoading(result.loading);
        previousSearchRef.current = result.previousSearch;
        setShowDropdown(result.showDropdown);
      }
    };

    setIsLoading(true);
    updateState();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedSearchValue, search]);

  // Handle input change
  const handleChange = (value: string) => {
    setSearchValue(value);
    setActiveIndex(-1);
  };

  // Handle result selection
  const handleSelect = (result: TypeaheadResult) => {
    onSelect(result);
    setSearchValue('');
    setActiveIndex(-1);
    setShowDropdown(false);
    inputRef.current?.blur();
  };

  // Keyboard navigation
  const handleKeyDown = (e: KeyboardEvent) => {
    const results = externalResults || internalResults;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderDefaultItem = (item: TypeaheadResult, _index: number, isActive: boolean, section: Section) => {
    return (
      <li
        key={`${item.type}-${item.id}-${item.personId}`}
        onClick={() => handleSelect(item)}
        className={`px-4 py-4 cursor-pointer ${isActive ? THEME_CLASSES.hover.background : ''} ${THEME_CLASSES.hover.background} hover:bg-opacity-90 transition-all duration-150 flex items-center ${section.itemClassName || ''}`}
      >
        <Avatar
          size={32}
          type={item.type === 'Account' || item.type === 'Contact' ? item.type : undefined}
          teamId={item.teamId}
          personId={String(item.personId || '')}
          className="rounded-full flex-shrink-0"
        />
        <div className="ml-3 flex-grow flex items-center">
          <div className="flex flex-col">
            <div
              className={`text-base font-semibold ${THEME_CLASSES.input.text}`}
              title={item.type === 'Contact' && item.accountName ? item.accountName : undefined}
            >
              {highlightText(item.name, searchValue.trim().split(/\s+/).filter(term => term.length > 0))}
            </div>
            {item.type === 'Contact' && item.accountName && (
              <div className={`text-xs ${THEME_CLASSES.text.secondary}`}>
                {item.accountName}
              </div>
            )}
          </div>
          {/*renderTypeChip({ rlpCompanyId: ((typeof item.teamId !== 'number') ? 1 : 0 ), teamId: item.teamId || 0, rlpPersonId: 0 })*/}
        </div>
      </li>
    );
  };

  return (
    <div ref={searchContainerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel}
          autoFocus={autoFocus}
          className={`w-full pl-10 pr-10 py-2 rounded-md text-sm focus:outline-none focus:ring-1 ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder} ${inputClassName}`}
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoading ? (
            <div className="animate-spin h-5 w-5 text-gray-400 border-2 border-gray-400 border-t-transparent rounded-full" />
          ) : (
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </div>
      </div>

      {showDropdown && (
        <div className={`absolute z-50 w-full mt-2 rounded-md shadow-2xl ring-1 ring-black ring-opacity-5 ${THEME_CLASSES.input.background} ${THEME_CLASSES.border}`}>
          {sections.map((section) => {
            const sectionItems = (externalResults || internalResults).filter(item => {
              if (item.type !== section.filter.type) return false;
              
              // Handle suggested contact filtering (fake IDs >= 99000000)
              if (section.filter.isSuggested !== undefined) {
                const isSuggestedContact = item.id >= 99000000;
                return isSuggestedContact === section.filter.isSuggested;
              }
              
              // Legacy hasId filtering for backward compatibility
              if (section.filter.hasId !== undefined) {
                return !!item.id === section.filter.hasId;
              }
              
              return true;
            });

            if (sectionItems.length === 0) return null;

            return (
              <React.Fragment key={section.title}>
                <div className={`px-4 py-2 text-xs font-semibold ${THEME_CLASSES.input.text} bg-gray-50 dark:bg-gray-800`}>
                  {section.title}
                </div>
                <ul className="py-2">
                  {sectionItems.map((item, index) => (
                    <div key={`${item.type}-${item.id}-${item.personId}`} onClick={() => handleSelect(item)}>
                      {section.renderItem?.(item, index, index === activeIndex, searchValue) ||
                        renderDefaultItem(item, index, index === activeIndex, section)}
                    </div>
                  ))}
                </ul>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {error && (
        <div className={`text-sm mt-1 ${THEME_CLASSES.text.secondary}`}>{error}</div>
      )}
    </div>
  );
};

export default Typeahead;
